import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

/**
 * ConfigOutline — bridges the ProjectConfigPage section list into the AppShell
 * sidebar. The config page (mockup project-config.html L344-L381) shows its
 * 6 sections as a nested sub-nav under the active "Config" item, each with a
 * scroll-spy active state and a per-section "modified" dot.
 *
 * ProjectConfigPage publishes its outline on mount (and on every dirty /
 * active-section change) and clears it on unmount. The AppShell sidebar reads
 * it. When the config page isn't mounted the outline is empty and no sub-nav
 * renders.
 */

export interface ConfigOutlineSection {
  /** DOM id of the section card — also the scroll-spy anchor target. */
  id: string;
  label: string;
  dirty: boolean;
}

export interface ConfigOutlineState {
  sections: ConfigOutlineSection[];
  activeId: string;
}

interface ContextValue {
  outline: ConfigOutlineState;
  setOutline: (next: ConfigOutlineState) => void;
}

const EMPTY: ConfigOutlineState = { sections: [], activeId: "" };

/** No-op fallback so the config page works in tests without a provider. */
const NOOP: ContextValue = { outline: EMPTY, setOutline: () => {} };

const ConfigOutlineContext = createContext<ContextValue | null>(null);

export function ConfigOutlineProvider({ children }: { children: ReactNode }) {
  const [outline, setOutlineState] = useState<ConfigOutlineState>(EMPTY);
  const setOutline = useCallback(
    (next: ConfigOutlineState) => setOutlineState(next),
    [],
  );
  return (
    <ConfigOutlineContext.Provider value={{ outline, setOutline }}>
      {children}
    </ConfigOutlineContext.Provider>
  );
}

/** Returns the outline context, or a no-op fallback when no provider exists. */
export function useConfigOutline(): ContextValue {
  return useContext(ConfigOutlineContext) ?? NOOP;
}
